import React, { useEffect, useRef, lazy, Suspense, memo } from 'react';
import { useGameStore } from './store/gameStore';
import { Hand, MultiHand } from './components/Hand';
import { GameControls } from './components/GameControls';
import { CountDisplay } from './components/CountDisplay';
import { SettingsPanel } from './components/SettingsPanel';
import { CountCheckModal } from './components/CountCheckModal';
import { Footer } from './components/Footer';
import { GameSettings } from './types';
import { Tutorial } from './components/Tutorial';
import { MobileGameControls } from './components/MobileGameControls';
import { ScreenReaderAnnouncer } from './components/ScreenReaderAnnouncer';
import BettingChips from './components/BettingChips';
import { CountTracker } from './components/common/CountTracker';
import { TableSurface } from './components/common/TableSurface';
import {
  ShieldIcon,
  CheckCircleIcon,
  XCircleIcon,
  CheckIcon,
  XIcon,
  WarningIcon,
  WalletXIcon,
  BettingIcon,
  ShuffleIcon,
} from './components/Icons';
import { AuthPanel } from './components/AuthPanel';
import { AdminPanelModal } from './components/AdminPanelModal';
import LandingPage from './pages/LandingPage';
const PartnersPage = lazy(() => import('./pages/PartnersPage'));
import { useAuth } from './auth/useAuth';
import { canAccessMode, shouldShowProBadge } from './utils/entitlements';
import { consumeCheckoutResult } from './services/billing';
import { consumeDemoLogin, getDemoKeyFromUrl } from './services/demoAuth';
import { ProBadge } from './components/billing/ProBadge';
import { Paywall } from './components/billing/Paywall';
import { BillingSettings } from './components/billing/BillingSettings';
import type { GameMode } from './config/plans';

// Lazy load heavy components for better initial load performance
const BlackjackTable = lazy(() => import('./components/BlackjackTable').then(m => ({ default: m.BlackjackTable })));
const EdgeCalculator = lazy(() => import('./components/EdgeCalculator').then(m => ({ default: m.EdgeCalculator })));
const SimulatorPage = lazy(() => import('./components/SimulatorPage').then(m => ({ default: m.SimulatorPage })));
const PracticeScenarios = lazy(() => import('./components/PracticeScenarios').then(m => ({ default: m.PracticeScenarios })));
const SpeedDrill = lazy(() => import('./components/SpeedDrill').then(m => ({ default: m.SpeedDrill })));
const ProgressPage = lazy(() => import('./components/ProgressPage').then(m => ({ default: m.ProgressPage })));

// Loading fallback component
const LoadingFallback = memo(() => (
  <div className="flex items-center justify-center min-h-[400px]">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500 mx-auto mb-4"></div>
      <div className="text-gray-400">Loading...</div>
    </div>
  </div>
));

interface ChipVisual {
  value: number;
  color: string;
  textColor: string;
  pattern: string;
}

const stackStripe = (stripe: string, fill: string) => [
  `radial-gradient(circle at center, ${fill} 0%, ${fill} 55%, transparent 55.5%)`,
  `repeating-conic-gradient(${stripe} 0deg 20deg, transparent 20deg 40deg)`,
  `radial-gradient(circle at center, ${fill} 0%, ${fill} 100%)`,
  'radial-gradient(circle at 30% 30%, rgba(255,255,255,0.12), transparent 48%)'
].join(', ');

const STACK_CHIPS: ChipVisual[] = [
  { value: 500, color: 'bg-purple-600 border-purple-800', textColor: 'text-white', pattern: stackStripe('rgba(255,255,255,0.18)', '#7c3aed') },
  { value: 100, color: 'bg-gray-800 border-gray-950', textColor: 'text-white', pattern: stackStripe('rgba(255,255,255,0.18)', '#111827') },
  { value: 25, color: 'bg-green-600 border-green-800', textColor: 'text-white', pattern: stackStripe('rgba(255,255,255,0.18)', '#16a34a') },
  { value: 5, color: 'bg-red-600 border-red-800', textColor: 'text-white', pattern: stackStripe('rgba(255,255,255,0.18)', '#dc2626') },
  { value: 1, color: 'bg-white border-gray-400', textColor: 'text-gray-800', pattern: stackStripe('rgba(0,0,0,0.08)', '#e5e7eb') }
];

function buildBetStack(amount: number): ChipVisual[] {
  const stack: ChipVisual[] = [];
  const chips = [...STACK_CHIPS].sort((a, b) => b.value - a.value);
  let remaining = Math.max(0, Math.round(amount));

  for (const chip of chips) {
    while (remaining >= chip.value && stack.length < 20) {
      stack.push(chip);
      remaining -= chip.value;
    }
  }

  return stack;
}

// Separate component for table simulator to properly use hooks
const TableSimulatorView: React.FC<{ settings: GameSettings }> = ({ settings }) => {
  const { tableSimDeck, tableState, triggerCountCheck, countCheck, tableReshuffle } = useGameStore();
  // Use table sim's separate deck state
  const dealtCards = tableSimDeck.dealtCards;
  const decksRemaining = (settings.numDecks - dealtCards.length / 52).toFixed(1);
  const lastCheckRef = useRef<number>(0);
  const handsPlayedRef = useRef<number>(0);

  // Random count check - trigger after random number of hands (3-8)
  useEffect(() => {
    if (tableState.dealingPhase === 'resolving' && !countCheck.isActive) {
      handsPlayedRef.current++;

      // Check if we should trigger a count check
      const handsSinceLastCheck = handsPlayedRef.current - lastCheckRef.current;
      const shouldCheck = handsSinceLastCheck >= 3 && Math.random() < 0.3; // 30% chance after 3+ hands

      if (shouldCheck || handsSinceLastCheck >= 8) { // Force check after 8 hands
        lastCheckRef.current = handsPlayedRef.current;
        // Small delay to let the round finish displaying
        setTimeout(() => {
          triggerCountCheck();
        }, 1500);
      }
    }
  }, [tableState.dealingPhase, triggerCountCheck, countCheck.isActive]);

  return (
    <div className="space-y-4">
      <CountTracker
        dealtCount={dealtCards.length}
        decksRemaining={decksRemaining}
        numDecks={settings.numDecks}
        penetration={settings.penetration}
        action={{
          label: 'Shuffle',
          onClick: tableReshuffle,
          icon: <ShuffleIcon size={18} className="transition-colors group-hover:text-white" />
        }}
        rightContent={<CountDisplay compact useTableSimDeck />}
      />

      {/* Full Width Table */}
      <BlackjackTable />
    </div>
  );
};

function TrainerApp() {
  const { user, loading, roles, effectiveProfile, profile, logout } = useAuth();
  const isAdmin = roles.includes('admin');
  const isDev = roles.includes('dev');
  const canAccessAdmin = isAdmin || isDev;
  // Use admin's branding if set on profile, otherwise fall back to settings
  const effectiveCasinoName = effectiveProfile?.casinoName;
  const {
    playerHands,
    currentHandIndex,
    dealerHand,
    isPlaying,
    isDealing,
    playerTurnComplete,
    roundComplete,
    bankroll,
    settings,
    dealCard,
    speedDrill,
    tickSpeedDrill,
    gameMode,
    setGameMode,
    lastDecisionAnalysis,
    lastBetAnalysis,
    lastInsuranceAnalysis,
    dealtCards,
    placeBet,
    dealNewHand,
    reshuffleShoe,
    insuranceBet,
    insuranceResult,
    insuranceOffered,
    hit,
    stand,
    doubleDown,
    split,
    surrender,
    takeInsurance,
    declineInsurance,
    statistics,
    debugDealDealerAceTraining,
    debugDealPlayerBJTraining,
    debugDealTableDealerAce,
    debugDealTablePlayerBJ,
    debugDealDealerBJTraining,
    debugDealTableDealerBJ,
    debugDealTableDealerTenUpBJ,
    debugDealTableDealerTenUpNonBJ,
    debugDealTablePlayerBust,
    debugDealTableSplitPair,
    debugDealTableDoubleThenBust,
    debugDealTablePush,
    debugDealTableEarlySurrender,
    debugDealTableLateSurrender,
    debugDealTableSplitAces21,
    debugDealTableLateSurrenderDealerBJ,
    debugDealDealerTenUpBJTraining,
    debugDealDealerTenUpNonBJTraining,
    debugDealPlayerBustTraining,
    debugDealSplitPairTraining,
    debugDealDoubleThenBustTraining,
    debugDealPushTraining,
    debugDealEarlySurrenderTraining,
    debugDealLateSurrenderTraining,
    debugDealSplitAces21Training,
    debugDealLateSurrenderDealerBJTraining,
  } = useGameStore();

  // Current total bet (used to gate dealing)
  const currentBetAmount = playerHands.reduce((sum, h) => sum + (h.bet || 0), 0);

  // Mobile menu state
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [showAdminPanel, setShowAdminPanel] = React.useState(false);
  const [showProfileDropdown, setShowProfileDropdown] = React.useState(false);

  // Billing: whether the current mode is locked behind Pro for this user.
  // With billing disabled, canAccessMode always returns true (no gating).
  const modeLocked = !canAccessMode(gameMode as GameMode, effectiveProfile);

  // Surface the Stripe checkout outcome once, on return from hosted checkout.
  const [checkoutResult, setCheckoutResult] = React.useState<'success' | 'cancel' | null>(null);
  useEffect(() => {
    const result = consumeCheckoutResult();
    if (result) {
      setCheckoutResult(result);
      const t = setTimeout(() => setCheckoutResult(null), 6000);
      return () => clearTimeout(t);
    }
  }, []);
  const trainingTableRef = React.useRef<HTMLDivElement | null>(null);
  const profileButtonRef = React.useRef<HTMLButtonElement | null>(null);

  // Chip-style button for Admin/Profile (matches nav buttons)
  const chipButtonClass = (isActive: boolean, color: 'gray' | 'purple' = 'gray') => {
    const baseColor = color === 'purple' 
      ? 'text-purple-300 hover:text-white' 
      : 'text-gray-400 hover:text-white';
    const activeStyles = color === 'purple'
      ? 'bg-gradient-to-br from-purple-600 to-purple-800 text-white'
      : 'bg-gradient-to-br from-emerald-600 to-emerald-800 text-white';
    return `relative px-4 py-1.5 rounded-full text-sm font-bold whitespace-nowrap flex items-center gap-1 overflow-hidden border-2 border-transparent transition-all duration-200 group ${
      isActive ? activeStyles : baseColor
    }`;
  };

  const shouldShowDecisionFeedback = Boolean(
    lastDecisionAnalysis && (!lastDecisionAnalysis.isCorrect || settings.feedbackMode === 'both')
  );
  const shouldShowBetFeedback = Boolean(
    lastBetAnalysis && (lastBetAnalysis.rating === 'poor' || settings.feedbackMode === 'both')
  );
  const activeBetStack = buildBetStack(currentBetAmount);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't handle shortcuts when typing in inputs or editable elements
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement ||
        e.target instanceof HTMLSelectElement ||
        (e.target as HTMLElement | null)?.isContentEditable
      ) {
        return;
      }

      // Don't handle during speed drill
      if (speedDrill.isActive) return;

      // Only handle in training mode
      if (gameMode !== 'training') return;

      const key = e.key.toLowerCase();
      const isDealKey = key === ' ' || key === 'spacebar' || key === 'enter';

      // Deal / start next hand (also allow before the first hand)
      if (isDealKey && !isDealing && !insuranceOffered && (!isPlaying || roundComplete)) {
        // Respect minimum bet before allowing deal via keyboard
        if (currentBetAmount < settings.minBet) {
          return;
        }
        e.preventDefault();
        dealNewHand();
        return;
      }

      // Insurance shortcuts
      if (insuranceOffered) {
        if (key === 'y' || key === 'i') {
          e.preventDefault();
          takeInsurance();
          return;
        }
        if (key === 'n') {
          e.preventDefault();
          declineInsurance();
          return;
        }
      }

      // Game action shortcuts (only during player's turn)
      if (isPlaying && !roundComplete && !playerTurnComplete && !isDealing && !insuranceOffered) {
        const currentHand = playerHands[currentHandIndex];

        switch (key) {
          case 'h': // Hit
            e.preventDefault();
            hit();
            break;
          case 's': // Stand
            e.preventDefault();
            stand();
            break;
          case 'd': // Double
            if (currentHand && currentHand.cards.length === 2 && currentHand.bet <= bankroll) {
              e.preventDefault();
              doubleDown();
            }
            break;
          case 'p': // Split
            if (currentHand && currentHand.cards.length === 2) {
              const [c1, c2] = currentHand.cards;
              const r1 = c1.rank === '10' || ['J', 'Q', 'K'].includes(c1.rank) ? '10' : c1.rank;
              const r2 = c2.rank === '10' || ['J', 'Q', 'K'].includes(c2.rank) ? '10' : c2.rank;
              if (r1 === r2 && currentHand.bet <= bankroll) {
                e.preventDefault();
                split();
              }
            }
            break;
          case 'r': // Surrender
            if (settings.surrenderAllowed && currentHand && currentHand.cards.length === 2 && playerHands.length === 1) {
              e.preventDefault();
              surrender();
            }
            break;
        }
      }

    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    isPlaying, isDealing, playerTurnComplete, roundComplete, insuranceOffered,
    hit, stand, doubleDown, split, surrender, takeInsurance, declineInsurance,
    dealNewHand, playerHands, currentHandIndex, bankroll, settings, gameMode, speedDrill.isActive,
    currentBetAmount
  ]);

  // Auto-scroll to the table on mobile when a hand starts
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const isMobile = window.innerWidth < 768;
    if (isMobile && isPlaying && trainingTableRef.current) {
      trainingTableRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [isPlaying]);

  // Speed Drill - Auto deal cards and timer
  useEffect(() => {
    if (!speedDrill.isActive || speedDrill.isPaused) return;

    // Deal cards automatically during speed drill using speedDrill.speed
    const dealInterval = setInterval(() => {
      dealCard();
    }, speedDrill.speed);

    // Timer countdown
    const timerInterval = setInterval(() => {
      tickSpeedDrill();
    }, 1000);

    return () => {
      clearInterval(dealInterval);
      clearInterval(timerInterval);
    };
  }, [speedDrill.isActive, speedDrill.isPaused, speedDrill.speed, dealCard, tickSpeedDrill]);

  // Auto-hide move feedback (decisions) after short delay
  useEffect(() => {
    if (gameMode !== 'training' || !lastDecisionAnalysis) return;
    const timeoutId = setTimeout(() => {
      useGameStore.setState({ lastDecisionAnalysis: null });
    }, 4000);
    return () => clearTimeout(timeoutId);
  }, [lastDecisionAnalysis, gameMode]);

  // Auto-hide bet feedback (including "Wrong Bet") after short delay
  useEffect(() => {
    if (gameMode !== 'training' || !lastBetAnalysis) return;
    const timeoutId = setTimeout(() => {
      useGameStore.setState({ lastBetAnalysis: null });
    }, 4000);
    return () => clearTimeout(timeoutId);
  }, [lastBetAnalysis, gameMode]);

  // Auto-hide insurance feedback after a short delay once shown
  useEffect(() => {
    if (!lastInsuranceAnalysis || insuranceOffered || gameMode !== 'training') return;
    const timeoutId = setTimeout(() => {
      useGameStore.setState({ lastInsuranceAnalysis: null });
    }, 4000);
    return () => clearTimeout(timeoutId);
  }, [lastInsuranceAnalysis, insuranceOffered, gameMode]);


  const showDebug = settings.devMode;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900 text-gray-200">
        <div className="text-center space-y-2">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-500 mx-auto" />
          <div>Checking authentication…</div>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 px-4">
        <div className="max-w-md w-full space-y-4">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-white">Blackjack Trainer</h1>
            <p className="text-sm text-gray-300">Sign in to access the trainer and admin tools.</p>
          </div>
          <AuthPanel />
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen flex flex-col bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 ${settings.highContrast ? 'high-contrast' : ''}`}>
      {/* Checkout result banner (shown briefly on return from Stripe) */}
      {checkoutResult && (
        <div
          className={`fixed top-4 left-1/2 -translate-x-1/2 z-[70] px-4 py-2.5 rounded-xl shadow-lg text-sm font-semibold border ${
            checkoutResult === 'success'
              ? 'bg-emerald-600 border-emerald-400 text-white'
              : 'bg-gray-800 border-gray-600 text-gray-200'
          }`}
          role="status"
        >
          {checkoutResult === 'success'
            ? '🎉 Welcome to Pro! Your subscription is active.'
            : 'Checkout canceled — no charge was made.'}
        </div>
      )}

      {/* Profile dropdown - no modal needed */}

      {/* Debug menu - dev only */}
      {showDebug && (
        <div className="fixed top-4 right-4 z-50 bg-black/40 text-white rounded-xl shadow-lg backdrop-blur-sm border border-white/10 px-3 py-2 space-y-2">
          <div className="text-xs font-semibold uppercase tracking-wide text-emerald-200">Debug</div>
          {gameMode === 'training' && (
            <div className="flex flex-col gap-1">
              <button
                className="px-3 py-1.5 rounded-lg bg-emerald-700 hover:bg-emerald-600 text-sm font-bold shadow"
                onClick={debugDealDealerAceTraining}
              >
                Deal Dealer Ace (Training)
              </button>
              <button
                className="px-3 py-1.5 rounded-lg bg-indigo-700 hover:bg-indigo-600 text-sm font-bold shadow"
                onClick={debugDealPlayerBJTraining}
              >
                Deal Player BJ (Training)
              </button>
              <button
                className="px-3 py-1.5 rounded-lg bg-rose-700 hover:bg-rose-600 text-sm font-bold shadow"
                onClick={debugDealDealerBJTraining}
              >
                Deal Dealer BJ (Training)
              </button>
              <button
                className="px-3 py-1.5 rounded-lg bg-amber-700 hover:bg-amber-600 text-sm font-bold shadow"
                onClick={debugDealDealerTenUpBJTraining}
              >
                Dealer 10 Up BJ (Training)
              </button>
              <button
                className="px-3 py-1.5 rounded-lg bg-amber-600 hover:bg-amber-500 text-sm font-bold shadow"
                onClick={debugDealDealerTenUpNonBJTraining}
              >
                Dealer 10 Up Non-BJ (Training)
              </button>
              <button
                className="px-3 py-1.5 rounded-lg bg-orange-700 hover:bg-orange-600 text-sm font-bold shadow"
                onClick={debugDealPlayerBustTraining}
              >
                Player Bust Setup
              </button>
              <button
                className="px-3 py-1.5 rounded-lg bg-yellow-700 hover:bg-yellow-600 text-sm font-bold shadow"
                onClick={debugDealSplitPairTraining}
              >
                Split Pair Setup
              </button>
              <button
                className="px-3 py-1.5 rounded-lg bg-lime-700 hover:bg-lime-600 text-sm font-bold shadow"
                onClick={debugDealDoubleThenBustTraining}
              >
                Double Then Bust
              </button>
              <button
                className="px-3 py-1.5 rounded-lg bg-cyan-700 hover:bg-cyan-600 text-sm font-bold shadow"
                onClick={debugDealPushTraining}
              >
                Push 20 vs 20
              </button>
              <button
                className="px-3 py-1.5 rounded-lg bg-teal-700 hover:bg-teal-600 text-sm font-bold shadow"
                onClick={debugDealEarlySurrenderTraining}
              >
                Early Surrender Test
              </button>
              <button
                className="px-3 py-1.5 rounded-lg bg-gray-700 hover:bg-gray-600 text-sm font-bold shadow"
                onClick={debugDealLateSurrenderTraining}
              >
                Late Surrender Test
              </button>
              <button
                className="px-3 py-1.5 rounded-lg bg-amber-500 hover:bg-amber-400 text-sm font-bold shadow"
                onClick={debugDealSplitAces21Training}
              >
                Split Aces 21 (No BJ)
              </button>
              <button
                className="px-3 py-1.5 rounded-lg bg-rose-500 hover:bg-rose-400 text-sm font-bold shadow"
                onClick={debugDealLateSurrenderDealerBJTraining}
              >
                Late Surrender Dealer BJ
              </button>
            </div>
          )}
          {gameMode === 'table-simulator' && (
            <div className="flex flex-col gap-1">
              <button
                className="px-3 py-1.5 rounded-lg bg-emerald-700 hover:bg-emerald-600 text-sm font-bold shadow"
                onClick={debugDealTableDealerAce}
              >
                Dealer Ace (Table)
              </button>
              <button
                className="px-3 py-1.5 rounded-lg bg-indigo-700 hover:bg-indigo-600 text-sm font-bold shadow"
                onClick={debugDealTablePlayerBJ}
              >
                Player BJ (Table)
              </button>
              <button
                className="px-3 py-1.5 rounded-lg bg-rose-700 hover:bg-rose-600 text-sm font-bold shadow"
                onClick={debugDealTableDealerBJ}
              >
                Dealer BJ (Table)
              </button>
              <button
                className="px-3 py-1.5 rounded-lg bg-amber-700 hover:bg-amber-600 text-sm font-bold shadow"
                onClick={debugDealTableDealerTenUpBJ}
              >
                Dealer 10 Up BJ (Table)
              </button>
              <button
                className="px-3 py-1.5 rounded-lg bg-amber-600 hover:bg-amber-500 text-sm font-bold shadow"
                onClick={debugDealTableDealerTenUpNonBJ}
              >
                Dealer 10 Up Non-BJ (Table)
              </button>
              <button
                className="px-3 py-1.5 rounded-lg bg-orange-700 hover:bg-orange-600 text-sm font-bold shadow"
                onClick={debugDealTablePlayerBust}
              >
                Player Bust (Table)
              </button>
              <button
                className="px-3 py-1.5 rounded-lg bg-yellow-700 hover:bg-yellow-600 text-sm font-bold shadow"
                onClick={debugDealTableSplitPair}
              >
                Split Pair (Table)
              </button>
              <button
                className="px-3 py-1.5 rounded-lg bg-lime-700 hover:bg-lime-600 text-sm font-bold shadow"
                onClick={debugDealTableDoubleThenBust}
              >
                Double Then Bust (Table)
              </button>
              <button
                className="px-3 py-1.5 rounded-lg bg-cyan-700 hover:bg-cyan-600 text-sm font-bold shadow"
                onClick={debugDealTablePush}
              >
                Push 20 vs 20 (Table)
              </button>
              <button
                className="px-3 py-1.5 rounded-lg bg-teal-700 hover:bg-teal-600 text-sm font-bold shadow"
                onClick={debugDealTableEarlySurrender}
              >
                Early Surrender (Table)
              </button>
              <button
                className="px-3 py-1.5 rounded-lg bg-gray-700 hover:bg-gray-600 text-sm font-bold shadow"
                onClick={debugDealTableLateSurrender}
              >
                Late Surrender (Table)
              </button>
              <button
                className="px-3 py-1.5 rounded-lg bg-amber-500 hover:bg-amber-400 text-sm font-bold shadow"
                onClick={debugDealTableSplitAces21}
              >
                Split Aces 21 (Table)
              </button>
              <button
                className="px-3 py-1.5 rounded-lg bg-rose-500 hover:bg-rose-400 text-sm font-bold shadow"
                onClick={debugDealTableLateSurrenderDealerBJ}
              >
                Late Surrender Dealer BJ (Table)
              </button>
            </div>
          )}
        </div>
      )}
      {/* Screen Reader Announcements */}
      <ScreenReaderAnnouncer />

      {/* Tutorial Modal */}
      <Tutorial />


      {/* Count Check Modal */}
      <CountCheckModal />

      {/* Header */}
      <header className="bg-gray-900 border-b border-gray-700 shadow-lg select-none">
        <div className="container mx-auto px-4 py-3 sm:py-6">
          {/* Mobile Header - Title with hamburger */}
          <div className="flex min-[900px]:hidden items-center justify-between">
            <h1 className="text-xl font-bold text-white">
              🎰 Blackjack Trainer
            </h1>
            
            {/* Mobile Hamburger Menu */}
            <div className="relative">
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="p-2 bg-gray-800 rounded-lg text-gray-300 hover:text-white transition-colors"
                aria-label="Menu"
              >
                {mobileMenuOpen ? (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                ) : (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                )}
              </button>
              
              {/* Mobile Dropdown Menu */}
              {mobileMenuOpen && (
                <div className="absolute right-0 top-12 z-50 bg-gray-800 rounded-2xl shadow-xl border border-gray-700 p-2 min-w-[200px]">
                  <div className="space-y-1">
                    {[
                      { mode: 'training', label: 'Hand Trainer' },
                      { mode: 'table-simulator', label: 'Full Table' },
                      { mode: 'practice', label: 'Scenarios' },
                      { mode: 'speed-drill', label: 'Speed Drill' },
                      { mode: 'edge-calculator', label: 'Edge Calc' },
                      { mode: 'simulator', label: 'Risk Sim' },
                      { mode: 'statistics', label: 'Stats' },
                    ].map(({ mode, label }) => (
                      <button
                        key={mode}
                        onClick={() => { setGameMode(mode as typeof gameMode); setMobileMenuOpen(false); }}
                        className="w-full text-left px-4 py-2.5 text-sm font-bold rounded-full relative overflow-hidden border-2 border-transparent"
                      >
                        {/* Chip background */}
                        <div 
                          className={`absolute inset-0 rounded-full transition-opacity duration-200 ${gameMode === mode ? 'opacity-100' : 'opacity-0'}`}
                          style={{ background: 'linear-gradient(135deg, #059669, #047857, #065f46)' }}
                        />
                        {/* Chip border overlay */}
                        <div 
                          className={`absolute inset-0 rounded-full border-2 transition-opacity duration-200 ${gameMode === mode ? 'opacity-100' : 'opacity-0'}`}
                          style={{ borderColor: 'rgba(16, 185, 129, 0.5)' }}
                        />
                        {/* Dashed inner ring */}
                        <div className={`absolute inset-0.5 rounded-full border border-dashed border-white/30 pointer-events-none transition-opacity duration-200 ${gameMode === mode ? 'opacity-100' : 'opacity-0'}`} />
                        {/* Label */}
                        <span className={`relative transition-colors duration-200 ${gameMode === mode ? 'text-white drop-shadow' : 'text-gray-300 hover:text-white'}`}>
                          {label}
                        </span>
                        {shouldShowProBadge(mode as GameMode, effectiveProfile) && (
                          <ProBadge className="relative ml-1.5" />
                        )}
                      </button>
                    ))}
                  </div>
                  <div className="border-t border-gray-700 my-2"></div>
                  <div className="px-2 pb-2 space-y-2">
                    {/* Mobile Profile Section */}
                    <div className="bg-gray-800/50 rounded-lg p-3 space-y-2">
                      <div className="text-xs text-gray-400">Signed in as</div>
                      <div className="text-white font-medium text-sm truncate">{profile?.email || 'Unknown'}</div>
                      <div className="text-xs text-gray-500">Roles: {roles.join(', ')}</div>
                      <BillingSettings />
                    <button
                        onClick={() => { setMobileMenuOpen(false); logout(); }}
                        className="w-full mt-2 px-3 py-2 text-sm font-medium text-red-400 hover:bg-red-900/30 hover:text-red-300 rounded-lg transition-colors flex items-center justify-center gap-2"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                      </svg>
                        Sign Out
                    </button>
                    </div>
                    
                    {/* Admin button for mobile - full width chip style */}
                    {canAccessAdmin && (
                      <button
                        onClick={() => { setShowAdminPanel(true); setMobileMenuOpen(false); }}
                        className="w-full text-left px-4 py-2.5 text-sm font-bold rounded-full relative overflow-hidden border-2 border-transparent group"
                      >
                        {/* Chip hover effect */}
                        <div className="absolute inset-0 rounded-full bg-gradient-to-br from-purple-600 to-purple-800 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                        <div className="absolute inset-0 rounded-full border-2 border-purple-400/50 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                        <div className="absolute inset-0.5 rounded-full border border-dashed border-white/30 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                        <span className="relative flex items-center gap-2 text-purple-300 group-hover:text-white transition-colors">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                          </svg>
                          Admin
                        </span>
                      </button>
                    )}
                    
                    {/* Settings - full width mode for mobile */}
                    <SettingsPanel fullWidth onOpen={() => setMobileMenuOpen(false)} />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Desktop Header */}
          <div className="hidden min-[900px]:flex flex-col xl:flex-row xl:justify-between xl:items-center gap-4">
            <div className="text-center xl:text-left">
              <h1 className="text-2xl lg:text-3xl xl:text-4xl font-bold text-white mb-1">
                🎰 Blackjack Card Counting Trainer
              </h1>
              <p className="text-sm lg:text-base text-gray-400">
                {gameMode === 'table-simulator'
                  ? 'Multi-Seat Table Simulator • Practice counting in a realistic casino environment'
                  : gameMode === 'speed-drill'
                    ? 'Speed Drill • Test your counting speed with rapid card dealing'
                    : gameMode === 'statistics'
                      ? 'Track your progress, XP, and achievements'
                      : gameMode === 'edge-calculator'
                        ? 'Calculate house edge based on table rules'
                        : gameMode === 'simulator'
                          ? 'See your projected win rate, hourly earnings, and risk of ruin'
                          : gameMode === 'practice'
                            ? 'Practice specific blackjack scenarios and decisions'
                            : 'Single Hand Training • Practice basic strategy and bet sizing'}
              </p>
            </div>

            <div className="flex justify-center xl:justify-end">
              {/* Desktop Mode Toggle with Settings */}
              <div className="flex justify-center gap-1.5 bg-gray-800/50 rounded-full p-1.5">
                {[
                  { mode: 'training', label: 'Hand Trainer' },
                  { mode: 'table-simulator', label: 'Full Table' },
                  { mode: 'practice', label: 'Scenarios' },
                  { mode: 'speed-drill', label: 'Speed Drill' },
                  { mode: 'edge-calculator', label: 'Edge Calc' },
                  { mode: 'simulator', label: 'Risk Sim' },
                  { mode: 'statistics', label: 'Stats' }, 
                ].map(({ mode, label }) => (
                  <button
                    key={mode}
                    onClick={() => setGameMode(mode as typeof gameMode)}
                    className="px-4 py-1.5 rounded-full text-sm font-bold whitespace-nowrap relative overflow-hidden border-2 border-transparent transition-[color,box-shadow] duration-200"
                  >
                    {/* Chip background - always present, fades in/out */}
                    <div 
                      className={`absolute inset-0 rounded-full transition-opacity duration-200 ${gameMode === mode ? 'opacity-100' : 'opacity-0'}`}
                      style={{ background: 'linear-gradient(135deg, #059669, #047857, #065f46)' }}
                    />
                    {/* Chip border overlay */}
                    <div 
                      className={`absolute inset-0 rounded-full border-2 transition-opacity duration-200 ${gameMode === mode ? 'opacity-100' : 'opacity-0'}`}
                      style={{ borderColor: 'rgba(16, 185, 129, 0.5)' }}
                    />
                    {/* Dashed inner ring */}
                    <div className={`absolute inset-0.5 rounded-full border border-dashed border-white/30 pointer-events-none transition-opacity duration-200 ${gameMode === mode ? 'opacity-100' : 'opacity-0'}`} />
                    {/* Label */}
                    <span className={`relative transition-colors duration-200 ${gameMode === mode ? 'text-white drop-shadow' : 'text-gray-400 hover:text-white'}`}>
                      {label}
                    </span>
                    {shouldShowProBadge(mode as GameMode, effectiveProfile) && (
                      <ProBadge className="relative ml-1.5" />
                    )}
                  </button>
                ))}
                {canAccessAdmin && (
                  <button
                    onClick={() => setShowAdminPanel(true)}
                    className={chipButtonClass(showAdminPanel, 'purple')}
                    title="Admin"
                  >
                    {/* Chip hover effect */}
                    <div className="absolute inset-0 rounded-full bg-gradient-to-br from-purple-600 to-purple-800 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                    <div className="absolute inset-0 rounded-full border-2 border-purple-400/50 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                    <div className="absolute inset-0.5 rounded-full border border-dashed border-white/30 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                    <svg className="w-4 h-4 relative z-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                    <span className="relative z-10 hidden min-[1060px]:inline group-hover:inline">Admin</span>
                  </button>
                )}
                <div className="relative">
                <button
                    ref={profileButtonRef}
                    onClick={() => setShowProfileDropdown(!showProfileDropdown)}
                    className={chipButtonClass(showProfileDropdown)}
                    title="Profile"
                  >
                    {/* Chip hover effect */}
                    <div className="absolute inset-0 rounded-full bg-gradient-to-br from-emerald-600 to-emerald-800 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                    <div className="absolute inset-0 rounded-full border-2 border-emerald-400/50 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                    <div className="absolute inset-0.5 rounded-full border border-dashed border-white/30 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                    <svg className="w-4 h-4 relative z-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    <span className="relative z-10 hidden min-[1060px]:inline group-hover:inline">Profile</span>
                  </button>
                  {/* Profile Dropdown */}
                  {showProfileDropdown && (
                    <>
                      <div 
                        className="fixed inset-0 z-40" 
                        onClick={() => setShowProfileDropdown(false)} 
                      />
                      <div className="absolute right-0 top-full mt-2 w-64 bg-gray-900 border border-gray-700 rounded-xl shadow-2xl z-50 overflow-hidden animate-page-in">
                        <div className="p-4 border-b border-gray-700">
                          <div className="text-sm text-gray-400">Signed in as</div>
                          <div className="text-white font-medium truncate">{profile?.email || 'Unknown'}</div>
                          <div className="text-xs text-gray-500 mt-1">
                            Roles: {roles.join(', ')}
                          </div>
                        </div>
                        <div className="px-4 pt-3">
                          <BillingSettings />
                        </div>
                        <div className="p-2">
                          <button
                            onClick={() => {
                              setShowProfileDropdown(false);
                              logout();
                            }}
                            className="w-full px-4 py-2.5 text-left text-sm font-medium text-red-400 hover:bg-red-900/30 hover:text-red-300 rounded-lg transition-colors flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                            Sign Out
                </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
                <SettingsPanel />
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Admin Panel Modal */}
      <AdminPanelModal isOpen={showAdminPanel} onClose={() => setShowAdminPanel(false)} />

      {/* Main Game Area */}
      <main className="flex-grow container mx-auto px-4 py-4 sm:py-8">
        <div key={gameMode} className="animate-page-in">
        {modeLocked ? (
          <Paywall mode={gameMode as GameMode} />
        ) : (
        <>
        {gameMode === 'table-simulator' ? (
          /* Table Simulator Mode - Full Width with Count Banner */
          <Suspense fallback={<LoadingFallback />}>
            <TableSimulatorView settings={settings} />
          </Suspense>
        ) : gameMode === 'speed-drill' ? (
          /* Speed Drill Mode - Unified View */
          <Suspense fallback={<LoadingFallback />}>
            <SpeedDrill />
          </Suspense>
        ) : gameMode === 'statistics' ? (
          /* Progress Mode - Unified Stats + Achievements */
          <Suspense fallback={<LoadingFallback />}>
            <ProgressPage />
          </Suspense>
        ) : gameMode === 'edge-calculator' ? (
          /* Edge Calculator Mode */
          <Suspense fallback={<LoadingFallback />}>
            <EdgeCalculator />
          </Suspense>
        ) : gameMode === 'simulator' ? (
          /* Monte Carlo Simulator Mode */
          <Suspense fallback={<LoadingFallback />}>
            <SimulatorPage />
          </Suspense>
        ) : gameMode === 'practice' ? (
          /* Practice Scenarios Mode */
          <Suspense fallback={<LoadingFallback />}>
            <div className="max-w-4xl mx-auto">
              <PracticeScenarios />
            </div>
          </Suspense>
        ) : (
          /* Training Mode - Single hand practice */
          <div className="space-y-4 pb-20 md:pb-0">
            <CountTracker
              dealtCount={dealtCards.length}
              decksRemaining={(settings.numDecks - dealtCards.length / 52).toFixed(1)}
              numDecks={settings.numDecks}
              penetration={settings.penetration}
              action={{
                label: 'Shuffle',
                onClick: reshuffleShoe,
                icon: <ShuffleIcon size={18} className="transition-colors group-hover:text-white" />
              }}
              rightContent={<CountDisplay compact />}
            />

            {/* Training Table */}
            <div ref={trainingTableRef}>
              <TableSurface>
                {/* Corner info overlays */}
                <div className="absolute top-2 left-2 sm:top-4 sm:left-4 z-10">
                  <div className="bg-green-900/70 border border-green-700/70 rounded-lg px-3 py-2 sm:px-3 sm:py-2.5 shadow-lg backdrop-blur-sm">
                    <div className="text-xs sm:text-sm text-gray-200/90">Bankroll</div>
                    <div className="text-2xl sm:text-3xl font-extrabold text-green-200 drop-shadow">${bankroll}</div>
                  </div>
                </div>
                <div className="absolute top-2 right-2 sm:top-4 sm:right-4 z-10 text-right">
                  <div className="bg-green-900/70 border border-green-700/70 rounded-lg px-3 py-2 sm:px-4 sm:py-3 shadow-lg backdrop-blur-sm">
                    <div className="text-xs sm:text-sm text-gray-200/90">Session</div>
                    <div className="flex flex-col items-end justify-end sm:flex-row sm:items-center sm:gap-2 text-lg sm:text-xl">
                      <span className="text-green-200 font-bold">W:{statistics.handsWon}</span>
                      <span className="text-red-200 font-bold">L:{statistics.handsLost}</span>
                      <span className="text-yellow-200 font-bold">P:{statistics.handsPushed}</span>
                    </div>
                    <div className="text-sm sm:text-base text-gray-100 font-semibold">
                      ${statistics.totalWon >= 0 ? '+' : ''}{statistics.totalWon}
                    </div>
                  </div>
                </div>
              
              {/* Dealer Hand */}
              <div className="mb-6 sm:mb-8 relative">
                <Hand
                  hand={dealerHand}
                  label="Dealer"
                  showValue={(isPlaying || roundComplete) && !settings.hideHandTotals}
                  showResult={false}
                />
              </div>

              {/* Game Controls */}
              <div className="my-4 sm:my-6 relative">
                <GameControls />
              </div>

              {/* Decision Feedback - show below buttons, during play and after round ends */}
              {shouldShowDecisionFeedback && lastDecisionAnalysis && (isPlaying || roundComplete) && (
                <div className="flex justify-center mb-4 relative fade-in-up">
                  <div className={`
                      px-4 py-2 rounded-lg shadow-lg border text-sm sm:text-base flex items-center gap-2 flex-wrap
                      ${lastDecisionAnalysis.isCorrect
                      ? 'bg-green-900/80 border-green-500 text-white'
                      : 'bg-red-900/80 border-red-500 text-white'}
                    `}>
                    {lastDecisionAnalysis.isCorrect
                      ? <CheckIcon size={18} className="text-green-300" />
                      : <XIcon size={18} className="text-red-300" />
                    }
                    <span className="font-bold">
                      {lastDecisionAnalysis.isCorrect ? 'Good!' : 'Mistake'}
                    </span>
                    <span className="opacity-90">
                      {' - '}{lastDecisionAnalysis.reason}
                    </span>
                    {!lastDecisionAnalysis.isCorrect && (
                      <span className="text-xs opacity-75">
                        (You: {lastDecisionAnalysis.playerAction}, Best: {lastDecisionAnalysis.bestAction})
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Player Hand(s) */}
              <div className="mb-6 sm:mb-8 relative">
                <MultiHand
                  hands={playerHands}
                  currentHandIndex={currentHandIndex}
                  label="Player"
                  showValue={!settings.hideHandTotals}
                  showResult={true}
                  isPlaying={isPlaying && !roundComplete}
                />
              </div>

              {/* Insurance Result Display */}
              {insuranceResult && (
                <div className="flex justify-center mb-4 relative">
                  <div className={`
                      px-4 py-2 rounded-lg shadow-lg border text-sm sm:text-base
                      ${insuranceResult === 'won'
                      ? 'bg-green-900/80 border-green-500 text-white'
                      : 'bg-red-900/80 border-red-500 text-white'}
                    `}>
                    <ShieldIcon size={18} className="mr-2 inline-block" />
                    <span className="font-bold">
                      Insurance {insuranceResult === 'won' ? 'Won!' : 'Lost'}
                    </span>
                    {insuranceResult === 'won' && (
                      <span className="ml-2 text-green-300">
                        +${insuranceBet * 2} profit
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Betting Section - Below Player Hand */}
              <div className="border-t border-green-800 pt-4 sm:pt-6 relative">
                <div className="flex flex-wrap items-end justify-center gap-4 sm:gap-6 max-w-5xl mx-auto">
                  {/* Bet Amount - Only show when playing */}
                  {isPlaying && (
                    <div className="bg-gray-900/80 border border-green-600/70 rounded-lg px-4 py-3 shadow-lg flex items-center gap-3">
                      <div className="flex -space-x-2">
                        {activeBetStack.length > 0 ? activeBetStack.map((chip, idx) => (
                          <div
                            key={`${chip.value}-${idx}`}
                            className={`relative w-9 h-9 rounded-full border-2 ${chip.color} ${chip.textColor} text-[11px] font-bold flex items-center justify-center shadow`}
                            style={{ backgroundImage: chip.pattern }}
                          >
                            <div className="absolute inset-[2px] rounded-full border border-white/30 opacity-60" />
                            <span className="relative">{chip.value}</span>
                          </div>
                        )) : (
                          <div className="w-9 h-9 rounded-full border-2 bg-gray-700 border-gray-600 flex items-center justify-center text-gray-300 text-xs">
                            0
                          </div>
                        )}
                      </div>
                      <div className="text-left">
                        <div className="text-xs text-gray-300 mb-1">
                          {playerHands.length > 1 ? 'Total Bets' : 'Current Bet'}
                        </div>
                        <div className="text-2xl sm:text-3xl font-bold text-emerald-200 drop-shadow-sm">
                          ${currentBetAmount || settings.minBet}
                        </div>
                        {insuranceBet > 0 && (
                          <div className="text-xs text-gray-300 mt-0.5">
                            +${insuranceBet} insurance
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Betting Chips - Only show when not playing or round complete */}
                  {(!isPlaying || roundComplete) && (
                    <div className="flex flex-col items-center">
                      <BettingChips
                        minBet={settings.minBet}
                        maxBet={settings.maxBet}
                        bankroll={bankroll}
                        onPlaceBet={placeBet}
                        disabled={bankroll <= 0}
                        currentBetValue={playerHands.reduce((sum, h) => sum + (h.bet || 0), 0)}
                        casinoName={effectiveCasinoName || settings.casinoName}
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Bet Sizing Feedback */}
              {shouldShowBetFeedback && lastBetAnalysis && isPlaying && (
                <div className="mt-4 text-center relative fade-in-up">
                  <div className={`inline-block px-4 py-2 rounded-lg ${lastBetAnalysis.rating === 'perfect' ? 'bg-green-900/80 border border-green-500' :
                    lastBetAnalysis.rating === 'good' ? 'bg-green-900/60 border border-green-600' :
                      lastBetAnalysis.rating === 'acceptable' ? 'bg-yellow-900/60 border border-yellow-600' :
                        'bg-red-900/60 border border-red-600'
                    }`}>
                    <div className="flex items-center gap-2">
                      {lastBetAnalysis.rating === 'perfect' ? (
                        <BettingIcon size={20} className="text-green-400" />
                      ) : lastBetAnalysis.rating === 'good' ? (
                        <CheckCircleIcon size={20} className="text-green-400" />
                      ) : lastBetAnalysis.rating === 'acceptable' ? (
                        <WarningIcon size={20} className="text-yellow-400" />
                      ) : (
                        <XCircleIcon size={20} className="text-red-400" />
                      )}
                      <span className={`font-semibold ${lastBetAnalysis.rating === 'perfect' || lastBetAnalysis.rating === 'good' ? 'text-green-400' :
                        lastBetAnalysis.rating === 'acceptable' ? 'text-yellow-400' : 'text-red-400'
                        }`}>
                        {lastBetAnalysis.rating === 'perfect' ? 'Perfect Bet!' :
                          lastBetAnalysis.rating === 'good' ? 'Good Bet' :
                            lastBetAnalysis.rating === 'acceptable' ? 'Acceptable' : 'Wrong Bet'}
                      </span>
                    </div>
                    <div className="text-sm text-gray-300 mt-1">
                      {lastBetAnalysis.feedback}
                    </div>
                    {lastBetAnalysis.rating !== 'perfect' && (
                      <div className="text-xs text-gray-400 mt-1">
                        Your bet: ${lastBetAnalysis.actualBet} | Optimal: ${lastBetAnalysis.optimalBet} ({lastBetAnalysis.percentageOff.toFixed(0)}% off)
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Bankrupt Display */}
              {bankroll <= 0 && (
                <div className="mt-6 text-center relative">
                  <div className="bg-red-900 bg-opacity-80 rounded-lg p-4 inline-block">
                    <div className="text-2xl font-bold text-red-200 mb-1 flex items-center justify-center gap-2">
                      <WalletXIcon size={24} className="text-red-300" />
                      Bankrupt!
                    </div>
                    <div className="text-gray-300 text-sm">Reset the game to continue</div>
                  </div>
                </div>
              )}
              </TableSurface>
            </div>

          {/* Keyboard Shortcuts Hint (desktop only) */}
          <div className="mt-4 hidden md:flex justify-center">
            <div className="inline-flex flex-wrap items-center justify-center gap-2 px-3 py-2 bg-black/40 border border-gray-700/70 rounded-2xl text-[11px] sm:text-xs text-gray-100 shadow-inner backdrop-blur-sm">
              <span className="flex items-center gap-1 font-semibold text-white pr-1">
                <span aria-hidden="true">⌨️</span> Shortcuts
              </span>
              {[
                ['H', 'Hit'],
                ['S', 'Stand'],
                ['D', 'Double'],
                ['P', 'Split'],
                ['R', 'Surrender'],
                ['Space', 'Deal'],
              ].map(([keyLabel, action]) => (
                <span
                  key={keyLabel}
                  className="flex items-center gap-1 px-2 py-1 rounded-full bg-gray-800/80 border border-gray-700 text-[11px] sm:text-xs leading-none"
                >
                  <span className="px-1.5 py-0.5 rounded bg-gray-900/80 text-emerald-200 font-mono text-[11px] sm:text-xs shadow-inner">
                    {keyLabel}
                  </span>
                  <span className="text-gray-200">{action}</span>
                </span>
              ))}
            </div>
          </div>

          </div>
        )}

        {/* Mobile Game Controls (swipe gestures) */}
        {gameMode === 'training' && (
          <>
            <MobileGameControls />
          </>
        )}
        </>
        )}
        </div>
      </main>

      {/* Footer */}
      <Footer
        accentColor="orange"
        disclaimer="Practice responsibly • Card counting is legal but casinos may ask you to leave"
        showTechStack={true}
        techStackText="For educational purposes only"
        brandName={effectiveCasinoName || settings.casinoName}
        brandLogoSrc="/jk-logo.svg"
      />
    </div>
  );
}

function App() {
  // Demo magic links (`/#demo=<key>`) sign the visitor in and drop them in the app.
  const [demoChecking, setDemoChecking] = React.useState<boolean>(() => !!getDemoKeyFromUrl());
  const [view, setView] = React.useState<'landing' | 'app' | 'partners'>(() => {
    if (typeof window === 'undefined') return 'landing';
    if (getDemoKeyFromUrl()) return 'app';
    if (window.location.hash === '#app') return 'app';
    if (window.location.hash === '#partners') return 'partners';
    return 'landing';
  });

  // Consume the demo key first, before the hash-sync effect can clear it.
  useEffect(() => {
    if (!demoChecking) return;
    consumeDemoLogin().finally(() => {
      setView('app');
      setDemoChecking(false);
    });
  }, [demoChecking]);

  useEffect(() => {
    if (demoChecking) return; // leave the URL alone until the demo key is consumed
    if (typeof window === 'undefined') return;
    if (view === 'app') {
      window.location.hash = '#app';
    } else if (view === 'partners') {
      window.location.hash = '#partners';
    } else {
      window.location.hash = '';
    }
  }, [view, demoChecking]);

  if (demoChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-gray-200">
        <div className="text-center space-y-3">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-500 mx-auto" />
          <div>Setting up your preview…</div>
        </div>
      </div>
    );
  }

  if (view === 'landing') {
    return (
      <LandingPage
        onEnterApp={() => setView('app')}
        onViewPartners={() => setView('partners')}
      />
    );
  }

  if (view === 'partners') {
    return (
      <Suspense fallback={<LoadingFallback />}>
        <PartnersPage onBack={() => setView('landing')} onEnterApp={() => setView('app')} />
      </Suspense>
    );
  }

  return <TrainerApp />;
}

export default App;
